import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";

// ============================================================================
// Service Worker registration + auto-update on deploy (iter-107e)
// ----------------------------------------------------------------------------
// The PWA is installed on a lot of phones / pinned tabs. Bumping JS/CSS alone
// isn't enough — the browser sticks with the previously-cached HTML shell
// until the old service worker decides to update, which can take ages. This
// block makes "redeploy = every user gets the new code on next page focus"
// without anyone needing to clear caches manually.
//
// Flow:
//   1. We register `/sw.js`. If a NEW SW is found waiting to activate, we
//      message it `SKIP_WAITING` so it takes over right away.
//   2. The new SW posts `SW_UPDATED` to every controlled page on activation
//      (see public/sw.js). We listen for that message and hard-reload the
//      page once — but only after we've ever been controlled, so the very
//      first install on a fresh device doesn't reload the user mid-load.
//   3. `controllerchange` is the same signal in the install→activate case;
//      we use it as a backup trigger.
// ============================================================================

function setupServiceWorker() {
  if (!('serviceWorker' in navigator)) return;

  let reloading = false;
  const safeReload = () => {
    if (reloading) return;
    reloading = true;
    // Use `replace` so the browser back-button doesn't loop on the old shell.
    window.location.reload();
  };

  // Track whether this page was ever controlled by a SW. On the very first
  // visit it isn't, so `controllerchange` would otherwise trigger an
  // unwanted reload right after the initial install.
  const hadController = !!navigator.serviceWorker.controller;

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (hadController) safeReload();
  });

  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data && event.data.type === 'SW_UPDATED' && hadController) {
      safeReload();
    }
  });

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then((registration) => {
        console.log('ServiceWorker registered:', registration.scope);

        // If there's already a waiting worker (e.g. the user reloaded the
        // page after a deploy), ask it to activate immediately.
        if (registration.waiting) {
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        }

        // Watch for updates that arrive while the tab is open.
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (!newWorker) return;
          newWorker.addEventListener('statechange', () => {
            if (
              newWorker.state === 'installed'
              && navigator.serviceWorker.controller
            ) {
              // A fresh SW is installed and ready to take over.
              newWorker.postMessage({ type: 'SKIP_WAITING' });
            }
          });
        });

        // Also check for updates whenever the user re-focuses the tab.
        // Catches the case where the PWA stayed open in the background
        // through an entire deploy.
        const checkForUpdate = () => {
          try {
            registration.update();
          } catch (e) {
            // ignore — best-effort
          }
        };
        window.addEventListener('focus', checkForUpdate);
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') checkForUpdate();
        });
      })
      .catch((error) => {
        console.log('ServiceWorker registration failed:', error);
      });
  });
}

setupServiceWorker();

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
